/**
 * Click to all data on screen.
 *
 * A screen interaction starts at the input event's own timestamp and completes
 * at the frame in which every data region of the target screen shows its data
 * or its empty state. First paint, the shell and skeletons do not count.
 *
 * Each data region reports its state through `useScreenRegion`. Regions belong
 * to the screen whose `ScreenRegionScope` encloses them, so a List table inside
 * Clinical Trials counts for Clinical Trials. Data painted from a cache counts
 * as on screen; the refresh behind it is marked separately as `settled`.
 */
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  GOLDEN_INTERACTION_ENTRY_PREFIX,
  observeGoldenInteractionLifecycle,
  startGoldenInteraction,
  type GoldenInteraction,
} from './goldenInteraction';
import { documentCodeFromNetwork } from './pageLoad';

/**
 * Home and every app it has a card for. A test keeps this in step with the Home
 * catalog, because an app without regions never completes its open.
 */
export const TRACKED_SCREEN_IDS = [
  'home',
  'patients',
  'clinical-trials',
  'marketing',
  'research',
  'feasibility',
  'trial-matcher',
  'calendar',
  'email',
  'team-chat',
  'video',
  'phone',
  'lists',
  'docs',
  'forms',
  'signing',
  'dashboards',
  'canvas',
  'templates',
  'training',
  'vault',
] as const;

export type ScreenId = (typeof TRACKED_SCREEN_IDS)[number];

/**
 * `cached` is data on screen that a refresh may still replace. It counts as on
 * screen unless the interaction asks for live data (login), and it delays
 * `settled` until the refresh lands.
 */
export type ScreenRegionState = 'loading' | 'cached' | 'ready' | 'empty' | 'error';

/**
 * The region state of a query. Data on screen stays `ready` or `empty` while a
 * refetch or a poll runs behind it, and a failed refetch keeps it too; only a
 * query with no data yet is `loading` or `error`.
 */
export function queryRegionState<T>(
  query: { readonly data: T | undefined; readonly isError: boolean },
  isEmpty: (data: T) => boolean
): ScreenRegionState {
  if (query.data !== undefined) return isEmpty(query.data) ? 'empty' : 'ready';
  return query.isError ? 'error' : 'loading';
}

export interface ScreenInteractionOptions {
  readonly screen: ScreenId;
  /** The input event that started the interaction; its timestamp is the start. */
  readonly event?: { readonly timeStamp?: number } | null;
  /** Start time on the performance timeline, used when there is no event. */
  readonly actionStartTimeMs?: number;
  /** Cached data does not complete the screen; only live data does. */
  readonly requireLive?: boolean;
}

const TRACKED_SCREENS: ReadonlySet<string> = new Set<ScreenId>(TRACKED_SCREEN_IDS);

// Long enough for a slow first sync of a stack; an interaction that never
// completes is dropped, not reported.
const COMPLETION_TIMEOUT_MS = 60_000;
const SETTLED_TIMEOUT_MS = 30_000;
const HELD_WORK_TIMEOUT_MS = 1_000;

interface RegionEntry {
  readonly regionId: string;
  readonly state: ScreenRegionState;
}

interface PendingScreen {
  readonly screen: ScreenId;
  readonly interaction: GoldenInteraction;
  readonly requireLive: boolean;
  confirming: boolean;
  frameId: number | null;
  timeoutId: ReturnType<typeof setTimeout> | null;
  /** Work for a settled screen, held until the click's frame is presented. */
  readonly held: (() => void)[];
}

const regionsByScreen = new Map<ScreenId, Map<string, RegionEntry>>();
const pendingByScreen = new Map<ScreenId, PendingScreen>();
// Clicks whose screen is complete and whose frame is not presented yet.
const presentingByScreen = new Map<ScreenId, PendingScreen>();
const settlingByScreen = new Map<ScreenId, PendingScreen>();
const completedScreens = new Set<ScreenId>();
// Screens whose view has rendered a data region in this document, on screen or
// kept hidden (warmed on Home, or left for another app).
const mountedScreens = new Set<ScreenId>();
const settledWaiters = new Map<ScreenId, Set<() => void>>();
const shownWaiters = new Map<ScreenId, Set<() => void>>();
// Background requests that no screen shows wait for the first screen to settle,
// so they do not queue in front of its data. A screen without regions (an app
// this module does not track) gives way after this long.
const FIRST_SCREEN_FALLBACK_MS = 3_000;
let firstScreenSettled = false;
const firstScreenListeners = new Set<() => void>();

const noPendingWaiters = new Set<() => void>();
const frameWaiters = new Set<() => void>();
const pendingListeners = new Set<(pending: boolean) => void>();
let reportedPending = false;

const FOREGROUND_DETAIL_INTERACTIONS = new Set([
  'list-open-usable-table',
  'patient-select-useful-record',
]);
const foregroundDetails = new Map<
  string,
  { deadline: ReturnType<typeof setTimeout>; releaseTask: ReturnType<typeof setTimeout> | null }
>();

function releaseForegroundDetail(runId: string): void {
  const pending = foregroundDetails.get(runId);
  if (!pending) return;
  clearTimeout(pending.deadline);
  if (pending.releaseTask !== null) clearTimeout(pending.releaseTask);
  foregroundDetails.delete(runId);
  releaseFrameWaiters();
  releaseNoPendingWaiters();
}

function holdForegroundDetail(key: string): void {
  foregroundDetails.set(key, {
    deadline: setTimeout(() => releaseForegroundDetail(key), HELD_WORK_TIMEOUT_MS),
    releaseTask: null,
  });
  notifyPendingChange();
}

function finishForegroundDetail(key: string): void {
  const pending = foregroundDetails.get(key);
  if (!pending || pending.releaseTask !== null) return;
  // Yield out of the presentation frame and let a newer click take priority too.
  pending.releaseTask = setTimeout(() => releaseForegroundDetail(key), 0);
}

// Lists and Records track their own presentation instead of starting another app open.
observeGoldenInteractionLifecycle(event => {
  if (!FOREGROUND_DETAIL_INTERACTIONS.has(event.interactionId)) return;
  if (event.phase === 'started') holdForegroundDetail(event.runId);
  else finishForegroundDetail(event.runId);
});

let nextDetailHold = 0;

/**
 * Hold the work no screen shows, such as serializing the replay, while a click
 * opens a detail inside a screen that has no measured interaction of its own,
 * such as a List's Record. Call the returned function once its frame is on
 * screen; the hold also ends after a second.
 */
export function holdForDetailOpen(): () => void {
  nextDetailHold += 1;
  const key = `detail:${nextDetailHold}`;
  holdForegroundDetail(key);
  return () => finishForegroundDetail(key);
}

/** Whether a click still waits for its screen, or for the frame that shows it. */
function screenFrameOutstanding(): boolean {
  return pendingByScreen.size > 0 || presentingByScreen.size > 0 || foregroundDetails.size > 0;
}

/** Run the work waiting for a click's frame, in a task after it, once none is outstanding. */
function releaseFrameWaiters(): void {
  if (screenFrameOutstanding() || frameWaiters.size === 0) return;
  const waiters = [...frameWaiters];
  frameWaiters.clear();
  setTimeout(() => waiters.forEach(waiter => waiter()), 0);
}

/**
 * Run after the frame a click is waiting for, for work that is on no screen's
 * critical path: the parts of the shell a screen does not need. While a click
 * waits for its screen (sign-in to Home, a reload of Home) the callback runs in
 * a task after that screen's presented frame; with none waiting, in a task after
 * the next frame. A hidden tab presents no frame, so it runs after a second at
 * the latest. Returns a cancel function.
 */
export function afterScreenFrame(callback: () => void): () => void {
  let done = false;
  let frameId: number | null = null;
  let taskId: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    if (done) return;
    done = true;
    cancelWaits();
    callback();
  };
  const latest = setTimeout(run, HELD_WORK_TIMEOUT_MS);
  const cancelWaits = () => {
    clearTimeout(latest);
    frameWaiters.delete(runUnlessWaiting);
    if (frameId !== null) globalThis.cancelAnimationFrame?.(frameId);
    if (taskId !== null) clearTimeout(taskId);
  };
  // A screen that started waiting meanwhile (a reload's Home mounts a commit
  // after the shell) is waited for too.
  const runUnlessWaiting = () => {
    if (done) return;
    taskId = null;
    if (screenFrameOutstanding()) frameWaiters.add(runUnlessWaiting);
    else run();
  };
  if (screenFrameOutstanding()) {
    frameWaiters.add(runUnlessWaiting);
  } else if (typeof globalThis.requestAnimationFrame === 'function') {
    frameId = globalThis.requestAnimationFrame(() => {
      frameId = null;
      taskId = setTimeout(runUnlessWaiting, 0);
    });
  } else {
    taskId = setTimeout(runUnlessWaiting, 0);
  }
  return () => {
    done = true;
    cancelWaits();
  };
}

/**
 * False until `afterScreenFrame` runs for this component, then true. It starts
 * waiting once `start` is true: a shell that renders a placeholder while the
 * session resolves starts when it renders the screen, so a reload's own
 * interaction, which the screen starts, is the one it waits for.
 */
export function useAfterScreenFrame(start = true): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready || !start) return;
    return afterScreenFrame(() => setReady(true));
  }, [ready, start]);
  return ready;
}

/** Forget a screen's pending open; work held for open screens runs once none is left. */
function endPending(screen: ScreenId): void {
  pendingByScreen.delete(screen);
  releaseFrameWaiters();
  releaseNoPendingWaiters();
}

function notifyPendingChange(): void {
  const pending = screenFrameOutstanding();
  if (pending === reportedPending) return;
  reportedPending = pending;
  pendingListeners.forEach(listener => listener(pending));
}

function releaseNoPendingWaiters(): void {
  notifyPendingChange();
  if (screenFrameOutstanding() || noPendingWaiters.size === 0) return;
  const waiters = [...noPendingWaiters];
  noPendingWaiters.clear();
  waiters.forEach(waiter => waiter());
}

function markFirstScreenSettled(): void {
  if (firstScreenSettled) return;
  firstScreenSettled = true;
  firstScreenListeners.forEach(listener => listener());
}

function subscribeFirstScreen(listener: () => void): () => void {
  firstScreenListeners.add(listener);
  return () => firstScreenListeners.delete(listener);
}

/**
 * Run once the first screen of this document shows live data (or after 3 s),
 * for background requests outside React. Returns a cancel function.
 */
export function whenFirstScreenSettled(callback: () => void): () => void {
  if (firstScreenSettled) {
    callback();
    return () => undefined;
  }
  const listener = () => {
    cancel();
    callback();
  };
  const timer = setTimeout(markFirstScreenSettled, FIRST_SCREEN_FALLBACK_MS);
  const cancel = () => {
    clearTimeout(timer);
    firstScreenListeners.delete(listener);
  };
  firstScreenListeners.add(listener);
  return cancel;
}

/** True once the first screen of this document shows live data (or after 3 s). */
export function useFirstScreenSettled(): boolean {
  const settled = useSyncExternalStore(
    subscribeFirstScreen,
    () => firstScreenSettled,
    () => true
  );
  useEffect(() => {
    if (settled) return;
    const timer = setTimeout(markFirstScreenSettled, FIRST_SCREEN_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [settled]);
  return settled;
}
let reloadInteractionChecked = false;

export function isTrackedScreen(value: string): value is ScreenId {
  return TRACKED_SCREENS.has(value);
}

function entryName(interaction: GoldenInteraction, name: string): string {
  return `${GOLDEN_INTERACTION_ENTRY_PREFIX}:${interaction.interactionId}:${interaction.runId}:${name}`;
}

function markEntry(interaction: GoldenInteraction, name: string, detail?: unknown): void {
  const performanceApi = globalThis.performance;
  if (typeof performanceApi?.mark !== 'function') return;
  try {
    performanceApi.mark(
      entryName(interaction, name),
      detail === undefined ? undefined : { detail }
    );
  } catch {
    performanceApi.mark(entryName(interaction, name));
  }
}

function measureFromStart(interaction: GoldenInteraction, name: string): void {
  const performanceApi = globalThis.performance;
  if (typeof performanceApi?.measure !== 'function') return;
  try {
    performanceApi.measure(
      entryName(interaction, `action-to-${name}`),
      entryName(interaction, 'action-start'),
      entryName(interaction, name)
    );
  } catch {
    // The start mark can be missing when the timeline was cleared.
  }
}

function eventStartTime(event: ScreenInteractionOptions['event']): number | undefined {
  const timeStamp = event?.timeStamp;
  const now = globalThis.performance?.now?.();
  if (typeof timeStamp !== 'number' || !Number.isFinite(timeStamp) || timeStamp <= 0) {
    return undefined;
  }
  // An event timestamp from another clock (old browsers used epoch time) is
  // later than now on the performance timeline; fall back to now then.
  if (typeof now === 'number' && timeStamp > now) return undefined;
  return timeStamp;
}

type Verdict = 'complete' | 'incomplete' | 'error';

function verdictFor(screen: ScreenId, requireLive: boolean): Verdict {
  const regions = regionsByScreen.get(screen);
  if (regions === undefined || regions.size === 0) return 'incomplete';
  let complete = true;
  for (const { state } of regions.values()) {
    if (state === 'error') return 'error';
    if (state === 'loading' || (requireLive && state === 'cached')) complete = false;
  }
  return complete ? 'complete' : 'incomplete';
}

function isSettled(screen: ScreenId): boolean {
  const regions = regionsByScreen.get(screen);
  if (regions === undefined || regions.size === 0) return false;
  for (const { state } of regions.values()) {
    if (state !== 'ready' && state !== 'empty') return false;
  }
  return true;
}

/**
 * Run the work held for a click's screen once its frame is on screen. A task
 * of its own lets the browser paint first.
 */
function releaseHeld(pending: PendingScreen): void {
  if (presentingByScreen.get(pending.screen) === pending) {
    presentingByScreen.delete(pending.screen);
    releaseFrameWaiters();
    releaseNoPendingWaiters();
  }
  if (pending.held.length === 0) return;
  const held = pending.held.splice(0);
  setTimeout(() => held.forEach(run => run()), 0);
}

function clearTimers(pending: PendingScreen): void {
  if (pending.frameId !== null && typeof globalThis.cancelAnimationFrame === 'function') {
    globalThis.cancelAnimationFrame(pending.frameId);
  }
  pending.frameId = null;
  if (pending.timeoutId !== null) clearTimeout(pending.timeoutId);
  pending.timeoutId = null;
}

function regionSummary(screen: ScreenId): Record<string, ScreenRegionState> {
  const summary: Record<string, ScreenRegionState> = {};
  for (const { regionId, state } of regionsByScreen.get(screen)?.values() ?? []) {
    summary[regionId] = state;
  }
  return summary;
}

function fail(pending: PendingScreen): void {
  clearTimers(pending);
  endPending(pending.screen);
  markEntry(pending.interaction, 'error', { regions: regionSummary(pending.screen) });
  pending.interaction.cancel();
  releaseHeld(pending);
}

function watchSettled(pending: PendingScreen): void {
  const previous = settlingByScreen.get(pending.screen);
  if (previous !== undefined) clearTimers(previous);
  settlingByScreen.set(pending.screen, pending);
  pending.timeoutId = setTimeout(() => {
    if (settlingByScreen.get(pending.screen) === pending) settlingByScreen.delete(pending.screen);
  }, SETTLED_TIMEOUT_MS);
  evaluateSettled(pending.screen);
}

function evaluateSettled(screen: ScreenId): void {
  const settling = settlingByScreen.get(screen);
  if (settling === undefined || !isSettled(screen)) return;
  clearTimers(settling);
  settlingByScreen.delete(screen);
  markEntry(settling.interaction, 'settled');
  measureFromStart(settling.interaction, 'settled');
}

function finish(pending: PendingScreen): void {
  clearTimers(pending);
  // Presenting first, so the end of the pending click does not count as its frame.
  presentingByScreen.set(pending.screen, pending);
  pending.timeoutId = setTimeout(() => releaseHeld(pending), HELD_WORK_TIMEOUT_MS);
  endPending(pending.screen);
  markEntry(pending.interaction, 'regions', { regions: regionSummary(pending.screen) });
  pending.interaction.markFrontendWorkComplete();
  void pending.interaction.complete('completion').then(presented => {
    clearTimers(pending);
    releaseHeld(pending);
    if (!presented) return;
    completedScreens.add(pending.screen);
    notifyShownWaiters(pending.screen);
    watchSettled(pending);
  });
}

function notifySettledWaiters(screen: ScreenId): void {
  if (!isSettled(screen)) return;
  const waiters = settledWaiters.get(screen);
  settledWaiters.delete(screen);
  const run = () => {
    markFirstScreenSettled();
    waiters?.forEach(waiter => waiter());
  };
  // What waits for a settled screen (background requests, the replay
  // recorder, the apps warmed behind Home) re-renders much of the shell. Run
  // in the commit that shows the data, it held that frame back by tens of
  // milliseconds after login, so a click still waiting for this screen gets
  // its frame first.
  const pending = pendingByScreen.get(screen) ?? presentingByScreen.get(screen);
  if (pending === undefined) {
    run();
    return;
  }
  // A hidden tab presents no frame; the held work does not wait for one.
  if (pending.held.push(run) === 1) {
    setTimeout(() => releaseHeld(pending), HELD_WORK_TIMEOUT_MS);
  }
}

/**
 * Run once every data region of a screen shows live data, for work that must
 * not compete with the screen itself. Returns a cancel function.
 */
export function whenScreenSettled(screen: ScreenId, callback: () => void): () => void {
  const waiters = settledWaiters.get(screen) ?? new Set<() => void>();
  settledWaiters.set(screen, waiters);
  waiters.add(callback);
  notifySettledWaiters(screen);
  return () => {
    waiters.delete(callback);
  };
}

function notifyShownWaiters(screen: ScreenId): void {
  const waiters = shownWaiters.get(screen);
  shownWaiters.delete(screen);
  waiters?.forEach(waiter => waiter());
}

/**
 * Run once the screen has presented its data, live or cached, without waiting
 * for other frames: for reads the screen put off so they would not slow its
 * own. Returns a cancel function.
 */
export function whenScreenCompleted(screen: ScreenId, callback: () => void): () => void {
  let done = false;
  const waiters = shownWaiters.get(screen) ?? new Set<() => void>();
  const run = () => {
    if (done) return;
    done = true;
    waiters.delete(run);
    callback();
  };
  shownWaiters.set(screen, waiters);
  waiters.add(run);
  if (completedScreens.has(screen)) run();
  return () => {
    done = true;
    waiters.delete(run);
  };
}

/**
 * Run once the screen has shown its data, live or cached, and no click waits
 * for its frame: for work that follows the screen without holding it back.
 * A screen that opened without a click runs it once it settles. Returns a
 * cancel function.
 */
export function whenScreenShown(screen: ScreenId, callback: () => void): () => void {
  let cancelWait: () => void = () => undefined;
  let cancelSettled: () => void = () => undefined;
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cancelShown();
    cancelSettled();
    cancelWait = whenNoScreenPending(callback);
  };
  const waiters = shownWaiters.get(screen) ?? new Set<() => void>();
  shownWaiters.set(screen, waiters);
  waiters.add(run);
  const cancelShown = () => waiters.delete(run);
  cancelSettled = whenScreenSettled(screen, run);
  if (done) cancelSettled();
  else if (completedScreens.has(screen)) run();
  return () => {
    done = true;
    cancelShown();
    cancelSettled();
    cancelWait();
  };
}

function evaluate(screen: ScreenId): void {
  evaluateSettled(screen);
  notifySettledWaiters(screen);
  const pending = pendingByScreen.get(screen);
  if (pending === undefined) return;
  const verdict = verdictFor(screen, pending.requireLive);
  if (verdict === 'error') {
    fail(pending);
    return;
  }
  if (verdict !== 'complete' || pending.confirming) return;
  if (typeof globalThis.requestAnimationFrame !== 'function') {
    finish(pending);
    return;
  }
  // Regions that mount in the same commit register before the next frame, so
  // one frame of grace catches a region that is still loading.
  pending.confirming = true;
  pending.frameId = globalThis.requestAnimationFrame(() => {
    pending.frameId = null;
    pending.confirming = false;
    if (pendingByScreen.get(screen) !== pending) return;
    const confirmed = verdictFor(screen, pending.requireLive);
    if (confirmed === 'error') fail(pending);
    else if (confirmed === 'complete') finish(pending);
  });
}

/** Start a click-to-complete-screen interaction for one screen. */
export function startScreenInteraction(
  interactionId: string,
  options: ScreenInteractionOptions
): GoldenInteraction {
  const previous = pendingByScreen.get(options.screen);
  if (previous !== undefined) {
    clearTimers(previous);
    pendingByScreen.delete(options.screen);
    previous.interaction.cancel();
    releaseHeld(previous);
  }
  const actionStartTimeMs = eventStartTime(options.event) ?? options.actionStartTimeMs;
  const interaction = startGoldenInteraction(
    interactionId,
    actionStartTimeMs === undefined ? {} : { actionStartTimeMs }
  );
  interaction.markFrontendWorkStart();
  const pending: PendingScreen = {
    screen: options.screen,
    interaction,
    requireLive: options.requireLive === true,
    confirming: false,
    frameId: null,
    timeoutId: null,
    held: [],
  };
  pending.timeoutId = setTimeout(() => {
    if (pendingByScreen.get(options.screen) !== pending) return;
    clearTimers(pending);
    endPending(options.screen);
    markEntry(interaction, 'timeout', { regions: regionSummary(options.screen) });
    interaction.cancel();
    releaseHeld(pending);
  }, COMPLETION_TIMEOUT_MS);
  pendingByScreen.set(options.screen, pending);
  notifyPendingChange();
  evaluate(options.screen);
  return interaction;
}

/** Whether a click still waits for its data or presentation frame. */
export function hasPendingScreenInteraction(): boolean {
  return screenFrameOutstanding();
}

/**
 * Run once no click waits for data or its presentation frame, for traffic no
 * screen shows, such as replay uploads and guidance events.
 * Runs at once when nothing is pending. Returns a cancel function.
 */
export function whenNoScreenPending(callback: () => void): () => void {
  if (!screenFrameOutstanding()) {
    callback();
    return () => undefined;
  }
  noPendingWaiters.add(callback);
  return () => {
    noPendingWaiters.delete(callback);
  };
}

/**
 * Call the listener with true when a click starts waiting for its screen, and
 * with false once no click waits for data or its presentation frame. Returns
 * an unsubscribe function.
 */
export function subscribeScreenPending(listener: (pending: boolean) => void): () => void {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

/** Drop the pending interaction of a screen, for an action that failed. */
export function cancelScreenInteraction(screen: ScreenId): void {
  const pending = pendingByScreen.get(screen);
  if (pending === undefined) return;
  clearTimers(pending);
  endPending(screen);
  pending.interaction.cancel();
  releaseHeld(pending);
}

/** Whether the screen has shown all its data once in this document. */
export function hasScreenCompleted(screen: string): boolean {
  return isTrackedScreen(screen) && completedScreens.has(screen);
}

/**
 * Whether the screen's view has rendered in this document, on screen or warmed
 * hidden. Its once-per-document reads have run and what it cached may still be
 * fresh, so opening it again can send fewer requests than its first mount.
 */
export function hasScreenMounted(screen: string): boolean {
  return isTrackedScreen(screen) && mountedScreens.has(screen);
}

/** Whether this region still has a mounted view, including a hidden view. */
export function hasScreenRegion(screen: string, regionId: string): boolean {
  if (!isTrackedScreen(screen)) return false;
  for (const region of regionsByScreen.get(screen)?.values() ?? []) {
    if (region.regionId === regionId) return true;
  }
  return false;
}

/**
 * Start the open of a tracked screen from a Home card or the rail. The phase is
 * `cached` once the screen has been complete in this document, `first` before.
 * Home has no first open: it is the screen login lands on.
 */
export function beginScreenOpen(
  screen: string,
  event?: ScreenInteractionOptions['event']
): string | null {
  if (!isTrackedScreen(screen)) return null;
  const cached = completedScreens.has(screen);
  if (screen === 'home' && !cached) return null;
  const interactionId = `${screen}-open-${cached ? 'cached' : 'first'}`;
  startScreenInteraction(interactionId, { screen, event });
  return interactionId;
}

/**
 * Start the open of a section inside a tracked screen, such as a Clinical
 * Trials tab. `cached` says the section has shown its data in this document
 * before. The regions of the section's own scope decide when it is complete.
 */
export function beginSectionOpen(
  screen: string,
  section: string,
  cached: boolean,
  event?: ScreenInteractionOptions['event']
): string | null {
  if (!isTrackedScreen(screen)) return null;
  const interactionId = `${screen}-${section}-tab-${cached ? 'cached' : 'first'}`;
  startScreenInteraction(interactionId, { screen, event });
  return interactionId;
}

/** Login click to Home with its live agenda on screen. */
export function beginLoginToHome(event?: ScreenInteractionOptions['event']): void {
  restartFirstScreen();
  startScreenInteraction('login-to-home-complete', { screen: 'home', event, requireLive: true });
}

/**
 * Make the screen a sign-in opens this document's first screen. The login form
 * settled the first screen already, or its fallback did while the person
 * typed, so what waits for it (the replay recorder, unread counts, tours)
 * would mount and send its requests in the commit that shows Home. It now
 * waits for Home, which settles it once its frame is on screen, or for the
 * fallback when the sign-in fails or lands elsewhere.
 */
function restartFirstScreen(): void {
  if (!firstScreenSettled) return;
  firstScreenSettled = false;
  // Only `useFirstScreenSettled` subscribers are listening while it is settled:
  // `whenFirstScreenSettled` runs its callback at once then.
  firstScreenListeners.forEach(listener => listener());
}

/**
 * A signed-in reload that lands on Home starts at the navigation itself
 * (time 0). Checked once per document, on the first Home render. A reload that
 * fetched the code from the network (a hard refresh) is its own interaction.
 */
export function beginReloadToHomeIfReloaded(): void {
  beginReloadIfReloaded('home');
}

/**
 * A signed-in reload that lands on an app, such as Clinical Trials, measured
 * like Home's: `<screen>-uncached-reload` for a hard refresh, `<screen>-reload`
 * for a reload from the browser cache.
 */
export function beginReloadToScreenIfReloaded(screen: ScreenId): void {
  beginReloadIfReloaded(screen);
}

function beginReloadIfReloaded(screen: ScreenId): void {
  if (reloadInteractionChecked) return;
  reloadInteractionChecked = true;
  if (pendingByScreen.has(screen) || completedScreens.size > 0) return;
  const navigation = globalThis.performance?.getEntriesByType?.('navigation')?.[0] as
    PerformanceNavigationTiming | undefined;
  if (navigation?.type !== 'reload') return;
  const fromNetwork = documentCodeFromNetwork() === true;
  const interactionId =
    screen === 'home'
      ? fromNetwork
        ? 'home-uncached-reload'
        : 'reload-to-home-complete'
      : `${screen}-${fromNetwork ? 'uncached-reload' : 'reload'}`;
  startScreenInteraction(interactionId, { screen, actionStartTimeMs: 0 });
}

/** Record one region's state; `useScreenRegion` calls this from a layout effect. */
export function reportScreenRegion(
  screen: ScreenId,
  key: string,
  regionId: string,
  state: ScreenRegionState
): void {
  let regions = regionsByScreen.get(screen);
  if (regions === undefined) {
    regions = new Map();
    regionsByScreen.set(screen, regions);
  }
  regions.set(key, { regionId, state });
  mountedScreens.add(screen);
  evaluate(screen);
}

/**
 * Forget a region that left the screen. The rest may now all show their data,
 * so the screen is checked again; a region that mounts in the same commit still
 * gets its frame of grace.
 */
export function removeScreenRegion(screen: ScreenId, key: string): void {
  const regions = regionsByScreen.get(screen);
  if (regions === undefined || !regions.delete(key)) return;
  if (regions.size === 0) {
    regionsByScreen.delete(screen);
    return;
  }
  evaluate(screen);
}

const ScreenScopeContext = createContext<ScreenId | null>(null);

/** Regions rendered inside count for this screen. Untracked screens are ignored. */
export function ScreenRegionScope({ screen, children }: { screen: string; children: ReactNode }) {
  return createElement(
    ScreenScopeContext.Provider,
    { value: isTrackedScreen(screen) ? screen : null },
    children
  );
}

/**
 * Report one data region of the enclosing screen. Call it with the state of
 * what is painted: `loading` while a skeleton or a placeholder shows, `cached`
 * for known data under a refresh, `ready` or `empty` for live data.
 */
export function useScreenRegion(regionId: string, state: ScreenRegionState): void {
  const screen = useContext(ScreenScopeContext);
  const key = useId();
  useLayoutEffect(() => {
    if (screen === null) return;
    reportScreenRegion(screen, key, regionId, state);
  }, [screen, key, regionId, state]);
  useLayoutEffect(() => {
    if (screen === null) return;
    return () => removeScreenRegion(screen, key);
  }, [screen, key]);
}

/** `useScreenRegion` as an element, for a region only one branch of a view renders. */
export function ScreenRegionReport({
  regionId,
  state,
}: {
  regionId: string;
  state: ScreenRegionState;
}): null {
  useScreenRegion(regionId, state);
  return null;
}

/** A region whose component is still loading, as a Suspense fallback. */
export function ScreenRegionPending({ regionId }: { regionId: string }): null {
  useScreenRegion(regionId, 'loading');
  return null;
}

export function resetScreenCompletionForTests(): void {
  for (const pending of foregroundDetails.values()) {
    clearTimeout(pending.deadline);
    if (pending.releaseTask !== null) clearTimeout(pending.releaseTask);
  }
  foregroundDetails.clear();
  for (const pending of pendingByScreen.values()) clearTimers(pending);
  for (const pending of presentingByScreen.values()) clearTimers(pending);
  for (const settling of settlingByScreen.values()) clearTimers(settling);
  regionsByScreen.clear();
  pendingByScreen.clear();
  presentingByScreen.clear();
  settlingByScreen.clear();
  completedScreens.clear();
  mountedScreens.clear();
  settledWaiters.clear();
  shownWaiters.clear();
  noPendingWaiters.clear();
  frameWaiters.clear();
  pendingListeners.clear();
  reportedPending = false;
  firstScreenSettled = false;
  firstScreenListeners.clear();
  reloadInteractionChecked = false;
}
